import { useState, useEffect } from "react";
import { Card, Button, Form, Table, Modal } from "react-bootstrap";
import Breadcrumb from "app/components/Breadcrumb";
import api from "app/services/api";

const MOVEMENT_FILTERS = ["all", "IN", "OUT"];

const formatDateTime = (value) =>
  value ? new Date(value).toLocaleString() : "-";

export default function Report() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Per-item detail
  const [detailProduct, setDetailProduct] = useState(null);
  const [detailLogs, setDetailLogs] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [movementFilter, setMovementFilter] = useState("all");

  useEffect(() => {
    fetchReport();
  }, []);

  const fetchReport = () => {
    setLoading(true);
    api
      .get("/api/stock/summary", { withCredentials: true })
      .then((res) => setRows(res.data.productDetails || []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  };

  const openDetail = (product, filter = "all") => {
    setDetailProduct(product);
    setMovementFilter(filter);
    setDetailLoading(true);
    setDetailLogs([]);

    api
      .get(`/api/stock/logs?productId=${encodeURIComponent(product.product_id)}`, {
        withCredentials: true,
      })
      .then((res) => setDetailLogs(res.data.logs || []))
      .catch(() => setDetailLogs([]))
      .finally(() => setDetailLoading(false));
  };

  const filteredRows = rows.filter((item) => {
    const term = search.toLowerCase();
    return (
      (item.product_name || "").toLowerCase().includes(term) ||
      (item.category_name || "").toLowerCase().includes(term)
    );
  });

  const totalRows = filteredRows.length;
  const totalPages = rowsPerPage >= totalRows ? 1 : Math.ceil(totalRows / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedRows = filteredRows.slice(startIndex, startIndex + rowsPerPage);

  const filteredLogs = detailLogs.filter(
    (log) => movementFilter === "all" || String(log.movement_type) === movementFilter,
  );

  const detailInTotal = detailLogs
    .filter((log) => log.movement_type === "IN")
    .reduce((sum, log) => sum + Number(log.qty || 0), 0);
  const detailOutTotal = detailLogs
    .filter((log) => log.movement_type === "OUT")
    .reduce((sum, log) => sum + Number(log.qty || 0), 0);

  // Count pill used for the Inward / Outward columns.
  const CountPill = ({ value, color, onClick }) => (
    <span
      onClick={onClick}
      style={{
        display: "inline-block",
        minWidth: "36px",
        padding: "2px 8px",
        border: `1px solid ${color}`,
        borderRadius: "4px",
        color,
        cursor: "pointer",
        textAlign: "center",
      }}
    >
      {value || 0}
    </span>
  );

  return (
    <section>
      <Breadcrumb
        routeSegments={[
          { name: "Dashboard", path: "/" },
          { name: "Product Management", path: "/product" },
          { name: "Report" },
        ]}
      />

      <Card body className="mb-3">
        <div className="d-flex justify-content-between align-items-center">
          <Card.Title className="mb-0">Stock Report</Card.Title>

          <input
            type="text"
            placeholder="Search Product / Category"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setCurrentPage(1);
            }}
            style={{
              width: "250px",
              height: "38px",
              paddingLeft: "10px",
              border: "1px solid #d1d1d1",
            }}
          />
        </div>
      </Card>

      <Card body>
        <div style={{ overflowX: "auto" }}>
          <Table bordered hover className="align-middle">
            <thead style={{ backgroundColor: "#cfc7d9" }}>
              <tr>
                <th>Product Name</th>
                <th>Category Name</th>
                <th>Inward</th>
                <th>Outward</th>
                <th>Stock Quantity</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan="5" className="text-center">Loading...</td>
                </tr>
              )}

              {!loading && paginatedRows.length === 0 && (
                <tr>
                  <td colSpan="5" className="text-center">No products found.</td>
                </tr>
              )}

              {!loading &&
                paginatedRows.map((item) => (
                  <tr key={item.product_id}>
                    <td
                      style={{ cursor: "pointer", color: "#663399" }}
                      onClick={() => openDetail(item)}
                      title="View item report"
                    >
                      {item.product_name || "-"}
                    </td>
                    <td>{item.category_name || "-"}</td>
                    <td>
                      <CountPill
                        value={item.in_count}
                        color="#1f6fd0"
                        onClick={() => openDetail(item, "IN")}
                      />
                    </td>
                    <td>
                      <CountPill
                        value={item.out_count}
                        color="#c4302b"
                        onClick={() => openDetail(item, "OUT")}
                      />
                    </td>
                    <td>{item.current_stock || 0}</td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </div>

        <div className="d-flex justify-content-end align-items-center mt-3">
          <span className="me-2">Rows per page</span>
          <Form.Select
            value={rowsPerPage}
            onChange={(e) => {
              setRowsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            style={{ width: "90px", marginRight: "15px" }}
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
            <option value={filteredRows.length}>All</option>
          </Form.Select>

          <Button
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(currentPage - 1)}
          >
            Prev
          </Button>
          <span className="mx-3">
            {currentPage} / {totalPages || 1}
          </span>
          <Button
            disabled={currentPage >= totalPages}
            onClick={() => setCurrentPage(currentPage + 1)}
          >
            Next
          </Button>
        </div>
      </Card>

      {/* Overall report for one item */}
      <Modal
        show={!!detailProduct}
        onHide={() => setDetailProduct(null)}
        size="lg"
        scrollable
      >
        <Modal.Header closeButton style={{ backgroundColor: "#cfc7d9" }}>
          <Modal.Title>
            Item Report - {detailProduct?.product_name || ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div>
              <strong>Total Inward:</strong> {detailInTotal}
              <span className="mx-3">|</span>
              <strong>Total Outward:</strong> {detailOutTotal}
              <span className="mx-3">|</span>
              <strong>Stock:</strong> {detailInTotal - detailOutTotal}
            </div>

            <Form.Select
              value={movementFilter}
              onChange={(e) => setMovementFilter(e.target.value)}
              style={{ width: "150px" }}
            >
              {MOVEMENT_FILTERS.map((option) => (
                <option key={option} value={option}>
                  {option === "all" ? "All Movements" : option}
                </option>
              ))}
            </Form.Select>
          </div>

          <Table bordered hover size="sm">
            <thead style={{ backgroundColor: "#cfc7d9" }}>
              <tr>
                <th>#</th>
                <th>Date / Time</th>
                <th>Mode</th>
                <th>Qty</th>
                <th>Supplier</th>
                <th>Remark</th>
                <th>Created By</th>
              </tr>
            </thead>
            <tbody>
              {detailLoading && (
                <tr>
                  <td colSpan="7" className="text-center">Loading logs...</td>
                </tr>
              )}

              {!detailLoading && filteredLogs.length === 0 && (
                <tr>
                  <td colSpan="7" className="text-center">No movements found.</td>
                </tr>
              )}

              {!detailLoading &&
                filteredLogs.map((log, index) => (
                  <tr key={`${log.movement_type}-${log.id}-${index}`}>
                    <td>{index + 1}</td>
                    <td>{formatDateTime(log.date)}</td>
                    <td>
                      <span
                        className={
                          "badge " + (log.movement_type === "IN" ? "bg-success" : "bg-danger")
                        }
                      >
                        {log.movement_type}
                      </span>
                    </td>
                    <td>{log.qty ?? "-"}</td>
                    <td>{log.supplier_name || "-"}</td>
                    <td>{log.remark || "-"}</td>
                    <td>{log.created_by_name || "-"}</td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDetailProduct(null)}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </section>
  );
}
